import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, Copy, Link, Ticket } from 'lucide-react'
import { toast } from '@/hooks/use-toast'
import { invokeEdgeFunction } from '@/lib/edge-functions'
import { supabase } from '@/integrations/supabase/client'

interface Waiver {
  id: string
  code: string
  label: string | null
  expires_at: string
  used_at: string | null
  used_by: string | null
  league_id: string | null
  created_at: string
}

function getWaiverStatus(w: Waiver): { label: string; className: string } {
  if (w.used_at) return { label: 'Used', className: 'bg-zinc-100 dark:bg-zinc-800 text-zinc-500' }
  if (new Date(w.expires_at) < new Date()) return { label: 'Expired', className: 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' }
  return { label: 'Active', className: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' }
}

export default function AdminWaivers() {
  const queryClient = useQueryClient()
  const [label, setLabel] = useState('')
  const [expiresInDays, setExpiresInDays] = useState('30')
  const [generating, setGenerating] = useState(false)
  const [generatedLink, setGeneratedLink] = useState<string | null>(null)

  const { data: waivers = [], isLoading } = useQuery({
    queryKey: ['admin-waivers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('league_creation_waivers')
        .select('id, code, label, expires_at, used_at, used_by, league_id, created_at')
        .order('created_at', { ascending: false })
        .limit(50)
      if (error) throw error
      return (data ?? []) as Waiver[]
    },
  })

  async function handleGenerate() {
    setGenerating(true)
    setGeneratedLink(null)
    try {
      const { data, error } = await invokeEdgeFunction<{ waiver: Waiver; link: string }>('generate-waiver', {
        body: {
          label: label.trim() || undefined,
          expiresInDays: parseInt(expiresInDays, 10) || 30,
        },
      })
      if (error) throw new Error(error.message)
      setGeneratedLink(data!.link)
      setLabel('')
      queryClient.invalidateQueries({ queryKey: ['admin-waivers'] })
      toast({ title: 'Waiver link generated' })
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to generate waiver'
      toast({ title: 'Error', description: msg, variant: 'destructive' })
    } finally {
      setGenerating(false)
    }
  }

  function copyLink(link: string) {
    navigator.clipboard.writeText(link)
    toast({ title: 'Link copied to clipboard' })
  }

  const activeCount = waivers.filter(w => !w.used_at && new Date(w.expires_at) >= new Date()).length
  const usedCount = waivers.filter(w => w.used_at).length

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-xl font-semibold text-zinc-900 dark:text-zinc-100">Waiver Links</h1>
        <p className="text-sm text-zinc-500 mt-1">Generate single-use links that let someone create a league without paying.</p>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-4">
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 px-4 py-3 flex-1">
          <p className="text-xs text-zinc-500 font-medium uppercase tracking-wider">Active</p>
          <p className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100 mt-1">{activeCount}</p>
        </div>
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 px-4 py-3 flex-1">
          <p className="text-xs text-zinc-500 font-medium uppercase tracking-wider">Used</p>
          <p className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100 mt-1">{usedCount}</p>
        </div>
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 px-4 py-3 flex-1">
          <p className="text-xs text-zinc-500 font-medium uppercase tracking-wider">Total</p>
          <p className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100 mt-1">{waivers.length}</p>
        </div>
      </div>

      {/* Generate form */}
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5 space-y-3">
        <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Generate New Link</h3>
        <div className="flex items-end gap-3">
          <div className="flex-1 space-y-1">
            <label className="text-xs text-zinc-500 font-medium">Label (optional)</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Andrew's league"
              className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700
                         bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100
                         focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
            />
          </div>
          <div className="w-28 space-y-1">
            <label className="text-xs text-zinc-500 font-medium">Expires in</label>
            <select
              value={expiresInDays}
              onChange={(e) => setExpiresInDays(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-zinc-200 dark:border-zinc-700
                         bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100
                         focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
            >
              <option value="7">7 days</option>
              <option value="14">14 days</option>
              <option value="30">30 days</option>
              <option value="90">90 days</option>
            </select>
          </div>
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg
                       bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 shrink-0"
          >
            {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link className="h-3.5 w-3.5" />}
            Generate
          </button>
        </div>

        {generatedLink && (
          <div className="flex items-center gap-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 px-3 py-2">
            <code className="flex-1 text-xs text-emerald-800 dark:text-emerald-300 truncate">{generatedLink}</code>
            <button
              onClick={() => copyLink(generatedLink)}
              className="shrink-0 p-1 rounded hover:bg-emerald-100 dark:hover:bg-emerald-900/50 transition-colors"
            >
              <Copy className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
            </button>
          </div>
        )}
      </div>

      {/* Waivers table */}
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 overflow-hidden">
        {isLoading ? (
          <div className="px-5 py-12 flex justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
          </div>
        ) : waivers.length === 0 ? (
          <div className="px-5 py-12 text-center">
            <Ticket className="h-8 w-8 text-zinc-300 dark:text-zinc-600 mx-auto mb-2" />
            <p className="text-sm text-zinc-400">No waiver links generated yet</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-100 dark:border-zinc-800 text-xs text-zinc-500 uppercase tracking-wider">
                <th className="px-5 py-2.5 text-left font-medium">Label</th>
                <th className="px-5 py-2.5 text-left font-medium">Status</th>
                <th className="px-5 py-2.5 text-left font-medium">Created</th>
                <th className="px-5 py-2.5 text-left font-medium">Expires</th>
                <th className="px-5 py-2.5 text-right font-medium">Link</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
              {waivers.map((w) => {
                const status = getWaiverStatus(w)
                const link = `${window.location.origin}/register?code=${w.code}`
                return (
                  <tr key={w.id}>
                    <td className="px-5 py-2.5 text-zinc-900 dark:text-zinc-100">
                      {w.label || <span className="text-zinc-400 italic">No label</span>}
                    </td>
                    <td className="px-5 py-2.5">
                      <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded-full font-medium ${status.className}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="px-5 py-2.5 text-xs text-zinc-500">
                      {new Date(w.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </td>
                    <td className="px-5 py-2.5 text-xs text-zinc-500">
                      {new Date(w.expires_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </td>
                    <td className="px-5 py-2.5 text-right">
                      {!w.used_at && new Date(w.expires_at) >= new Date() && (
                        <button
                          onClick={() => copyLink(link)}
                          className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400 hover:underline"
                        >
                          <Copy className="h-3 w-3" />
                          Copy
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
